'use client';

import {
  Breakpoint,
  BreakpointAlias,
  breakpoints,
  getBreakpointKey,
} from '@/theme/config/breakpoints';

import { keys, mapObject } from '@/lib/helpers';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import React from 'react';

/**
 * If the window width is >= the provided {@link Breakpoint}
 *
 */
export const useBreakpoints = () => {
  /** An object listing if the window width is >= each {@link Breakpoint} */
  const [values, setValues] = React.useState<Record<Breakpoint, boolean>>();

  React.useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const newValues = mapObject(breakpoints, (breakpointMin) => windowWidth >= breakpointMin);
      setValues(newValues);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return values;
};

export const useBreakpointMin = (bp: Breakpoint | BreakpointAlias, initValue = true) => {
  /** An object listing if the window width is >= each {@link Breakpoint} */
  const values = useBreakpoints();
  return values ? values[getBreakpointKey(bp)] : initValue;
};

/** If the window width is < the provided {@link Breakpoint} */
export const useBreakpointMax = (bp: Breakpoint | BreakpointAlias, initValue = false) =>
  !useBreakpointMin(bp, initValue);

export type ResponsiveValue = string | null | undefined | null;
export type ResponsiveValues<T extends ResponsiveValue> = { [P in Breakpoint]?: T };

// export const isResponsiveValues = <T extends ResponsiveValue>(x: any): x is ResponsiveValues<T> => {}

export function useResponsiveValue(maybeResponsiveValues: undefined): undefined;
export function useResponsiveValue<T extends ResponsiveValue>(
  maybeResponsiveValues: ResponsiveValues<T> | T
): T;
export function useResponsiveValue<T extends ResponsiveValue>(
  maybeResponsiveValues?: ResponsiveValues<T>
): T | undefined {
  const breakpointStates = useBreakpoints();

  if (!maybeResponsiveValues || isString(maybeResponsiveValues) || isNumber(maybeResponsiveValues))
    return maybeResponsiveValues as T;

  const minBreakpointUsed = breakpointStates
    ? keys(breakpoints)
        .reverse()
        .find((breakpoint) => {
          return breakpointStates[breakpoint] && breakpoint in maybeResponsiveValues;
        })
    : undefined;

  return minBreakpointUsed ? maybeResponsiveValues[minBreakpointUsed] : undefined;
}
